import * as React from "react"
import { useScrollbarWidth } from "react-use"
import { themedValue } from "@digits-shared/themes"
import { DIGITS_BACKGROUND } from "@digits-shared/themes/background"
import colors from "@digits-shared/themes/colors"
import { GRADIENT_HALO } from "@digits-shared/themes/gradientHalo"
import fonts from "@digits-shared/themes/typography"
import { createGlobalStyle, css } from "styled-components"
import { GlobalStyles as SharedGlobalStyles } from "@digits-shared/initializers/globalStyles"
import "@digits-shared/polyfills/arrays"
// install compression polyfills if needed
import "@digits-shared/polyfills/streams/streams"

function addScrollBarStyles(color: string, scrollbarWidth: number | undefined) {
  const size = scrollbarWidth || 0
  // if size is 0, use native styles
  if (!size)
    return css`
      scrollbar-color: ${color} ${colors.transparent};
    `

  const doc = document.documentElement
  doc.style.setProperty("--scrollbar-width", `${size}px`)

  return css`
    *::-webkit-scrollbar-button,
    *::-webkit-scrollbar-corner {
      display: none;
    }

    *::-webkit-scrollbar {
      background-color: rgba(0, 0, 0, 0);
      width: ${size}px;
      height: ${size}px;
      -webkit-appearance: none;
    }

    *::-webkit-scrollbar-thumb {
      background-color: ${color};
      border: ${size / 4}px solid transparent;
      border-radius: ${size / 2}px;
      background-clip: padding-box;
    }

    scrollbar-color: ${color} ${colors.transparent};
  `
}

function addDigitsForMacStyles() {
  // Custom styles for Digits for Mac.
  // Disables selecting text for certain elements
  return css`
    .digits-mac {
      #springboard-header,
      #application-dock,
      #springboard-footer {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
      }

      input,
      textarea,
      div[contenteditable="true"] {
        -webkit-touch-callout: default;
        -webkit-user-select: auto;
      }
    }
  `
}

const globalStyles = themedValue({
  light: css<{ scrollbarWidth: number | undefined }>`
    color: ${colors.secondary};
    ${DIGITS_BACKGROUND};
    ${({ scrollbarWidth }) => addScrollBarStyles(colors.translucentSecondary10, scrollbarWidth)}
  `,
  dark: css<{ scrollbarWidth: number | undefined }>`
    color: ${colors.white};
    background-color: #000;
    background-image:
      radial-gradient(55% 60% at 50% 50%, #061d2b 0%, rgba(0, 209, 255, 0) 100%),
      linear-gradient(115deg, #0f2342 0%, #010609 100%);

    background-size: cover;
    background-repeat: no-repeat;
    ${({ scrollbarWidth }) => addScrollBarStyles("#04060d", scrollbarWidth)}
  `,
})

const AccountingGlobalStyles = createGlobalStyle<{ scrollbarWidth: number | undefined }>`
  ${addDigitsForMacStyles};

  :root {
    font-family: ${fonts.family.avenir};
    font-weight: ${fonts.weight.normal};

    ${globalStyles};
    font-synthesis: none;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    -webkit-text-size-adjust: 100%;
  }
`

export const GlobalStyles: React.FC = () => {
  const scrollbarWidth = useScrollbarWidth()
  return (
    <>
      <SharedGlobalStyles />
      <AccountingGlobalStyles scrollbarWidth={scrollbarWidth} />
    </>
  )
}

export const GlowStyleOverride = createGlobalStyle`
  :root {
    ${GRADIENT_HALO};
  }
`

export const PlgStyleOverride = createGlobalStyle`
  :root {
    background: ${colors.glowBackground};
    --module-loading-color: ${colors.secondary};
  }
`
